export const CHECKOUT_URL = "https://mandibular-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/34FRV3PtTkhuMtn89UWFdt/MOCKUP_MANDIBULAR-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/ky5pG8xSWZY1BPqtVDLKvX/VALERIA_MONTES_-_BIOMECANICA.png",
};

export const beforeItems = [
  "You struggle to determine why a lower denture lacks stability.",
  "You find yourself repeating adjustments without a clear diagnostic sequence.",
  "Severely resorbed mandibular ridges make treatment planning feel unpredictable.",
  "You're uncertain which anatomical structures require closer evaluation.",
  "You have difficulty deciding which impression or occlusal considerations deserve priority.",
  "You spend valuable time reviewing scattered information.",
];

export const afterItems = [
  "Follow a structured 15-minute initial case-organization workflow.",
  "Identify anatomical and functional factors that may compromise stability.",
  "Use clinical decision maps to organize possible causes of poor retention.",
  "Evaluate challenging mandibular cases using practical assessment checklists.",
  "Compare relevant impression, border extension, and occlusal considerations.",
  "Build a clearer, patient-specific treatment planning framework.",
];

export const situations = [
  {
    title: "The lower denture that won't stay in place",
    body: "Your patient returns because their mandibular denture moves while speaking or chewing. You make another adjustment, but you're still unsure which factor is contributing most to the instability.",
  },
  {
    title: "The severely resorbed mandibular ridge",
    body: "You examine a patient with limited residual ridge height. You know conventional support may be compromised, but deciding how to approach the case requires careful evaluation.",
  },
  {
    title: "The impression that looks right",
    body: "The impression appears acceptable. Yet during subsequent appointments, the denture demonstrates unexpected functional instability.",
  },
  {
    title: "The patient with unfavorable muscle dynamics",
    body: "Tongue movement, cheek pressure, or altered muscular coordination seems to interfere with denture stability. You need a more organized way to assess these influences.",
  },
  {
    title: "The occlusion question",
    body: "You're uncertain whether the current occlusal arrangement is contributing to denture displacement. You want to understand which factors should be evaluated before modifying the prosthesis.",
  },
  {
    title: "The case that doesn't fit the textbook",
    body: "The patient presents multiple anatomical and functional challenges. You know the principles, but translating them into a structured clinical plan feels difficult.",
  },
];

export const stabilityFactors = [
  "Residual ridge anatomy",
  "Denture-bearing tissue conditions",
  "Border extension",
  "Functional muscle activity",
  "Occlusal relationships",
  "Denture base adaptation",
  "Patient-specific neuromuscular factors",
];

export const volumes = [
  {
    number: 1,
    title: "Mandibular Anatomy & Clinical Assessment",
    subtitle: "Understand the anatomical foundations of lower denture stability",
    intro: "Before considering impression techniques or prosthetic modifications, you need to understand the anatomy you're working with.",
    points: [
      "Mandibular residual ridge anatomy and classification",
      "Primary and secondary denture-bearing areas",
      "Buccal shelf and retromolar pad considerations",
      "Mylohyoid and retromylohyoid anatomical relationships",
      "Sublingual and lingual sulcus assessment",
      "Soft tissue characteristics and their clinical implications",
      "Functional tongue position and surrounding musculature",
      "Anatomical warning signs requiring additional evaluation",
    ],
    outcome: "A structured approach to identifying relevant anatomical factors before developing a mandibular denture treatment plan.",
  },
  {
    number: 2,
    title: "Lower Denture Stability & Retention Analysis",
    subtitle: "Understand why mandibular dentures lose stability",
    intro: "Not every unstable denture has the same underlying problem. This volume helps you organize the possible contributing factors.",
    points: [
      "Differences between support, stability, and retention",
      "Biomechanical forces affecting mandibular dentures",
      "Potential causes of displacement during function",
      "Effects of residual ridge resorption",
      "Influence of tongue and cheek musculature",
      "Border extension considerations",
      "Denture base adaptation assessment",
      "Clinical decision maps for instability complaints",
    ],
    outcome: "A clearer framework for evaluating potential causes of lower denture instability.",
  },
  {
    number: 3,
    title: "Functional Impression Planning Blueprint",
    subtitle: "Organize impression decisions around patient anatomy and function",
    intro: "Impression procedures must account for the patient's tissues, anatomy, and functional requirements.",
    points: [
      "Preliminary impression assessment",
      "Custom tray design considerations",
      "Functional border molding principles",
      "Tissue displacement and pressure considerations",
      "Selection criteria for impression materials",
      "Functional movements relevant to border assessment",
      "Impression evaluation checklists",
      "Common impression-related problems and their possible implications",
    ],
    outcome: "A structured educational framework for comparing impression approaches and evaluating their clinical suitability.",
  },
  {
    number: 4,
    title: "Mandibular Denture Occlusion & Functional Balance",
    subtitle: "Connect occlusal planning with prosthetic stability",
    intro: "Even when the denture base appears acceptable, functional forces can contribute to displacement.",
    points: [
      "Vertical dimension assessment principles",
      "Centric relation considerations",
      "Interarch relationship evaluation",
      "Bilateral balanced occlusion",
      "Lingualized occlusion",
      "Monoplane occlusal concepts",
      "Factors influencing occlusal scheme selection",
      "Recognition of potential occlusal interferences",
      "Functional evaluation during prosthetic appointments",
    ],
    outcome: "A more organized understanding of how occlusal decisions relate to mandibular denture function.",
  },
  {
    number: 5,
    title: "Complex Mandibular Denture Case Planning",
    subtitle: "Approach difficult anatomical conditions with a clearer decision framework",
    intro: "Some mandibular denture cases require more extensive evaluation and consideration of alternative approaches.",
    points: [
      "Severely resorbed mandibular ridges",
      "Mobile and compromised denture-bearing tissues",
      "Unfavorable muscular attachments",
      "Reduced vestibular depth",
      "Challenging tongue posture and movement",
      "Limited supporting anatomy",
      "Factors affecting conventional denture prognosis",
      "When additional assessment or specialist referral may be appropriate",
      "Patient-specific treatment planning considerations",
    ],
    outcome: "A structured framework for recognizing clinical complexity and organizing appropriate next steps.",
  },
  {
    number: 6,
    title: "Mandibular Denture Delivery & Troubleshooting",
    subtitle: "Know what to evaluate when a lower denture isn't performing as expected",
    intro: "The clinical process doesn't end when the denture is delivered. This volume focuses on systematic evaluation during delivery and follow-up.",
    points: [
      "Initial insertion assessment",
      "Functional stability evaluation",
      "Pressure-related discomfort assessment",
      "Common post-delivery complaints",
      "Potential causes of repeated displacement",
      "Occlusal evaluation during follow-up",
      "Patient adaptation considerations",
      "Documentation and reassessment checklists",
      "Decision pathways for additional evaluation, modification, or referral",
    ],
    outcome: "A more systematic approach to evaluating post-delivery problems rather than relying on repeated adjustments without a clear assessment sequence.",
  },
];

export const tools = [
  {
    title: "Mandibular Anatomy Assessment Checklist",
    body: "A structured checklist for documenting relevant anatomical findings before treatment planning.",
  },
  {
    title: "Lower Denture Instability Decision Tree",
    body: "A visual guide to organize possible contributors to denture movement and determine which factors require further evaluation.",
  },
  {
    title: "Functional Impression Planning Matrix",
    body: "A comparison framework for reviewing impression-related considerations in different clinical situations.",
  },
  {
    title: "Occlusal Assessment Reference Chart",
    body: "A practical comparison of relevant occlusal principles and their potential implications for mandibular denture function.",
  },
  {
    title: "Post-Delivery Troubleshooting Worksheet",
    body: "A structured documentation tool for recording complaints, clinical findings, reassessment priorities, and follow-up decisions.",
  },
];

export const bonuses = [
  {
    number: 1,
    title: "The Mandibular Denture Case Assessment Workbook™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/h1axsfWkNKW4WcMYkSiLxT/BONUS_1_MANDIBULAR-EN.png",
    description: "A practical workbook designed to help you organize patient information before developing a treatment plan.",
    includes: [
      "Patient assessment worksheets",
      "Mandibular anatomy documentation templates",
      "Functional findings checklist",
      "Clinical concern prioritization sheets",
      "Treatment planning summary pages",
    ],
    why: "Keep relevant clinical findings organized in one consistent format.",
  },
  {
    number: 2,
    title: "The Lower Denture Instability Troubleshooting Atlas™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/rSZPTshvBtmF7BCg6Yc99V/BONUS_2_MANDIBULAR-EN.png",
    description: "A visual reference for reviewing common mandibular denture complaints and their possible contributing factors.",
    includes: [
      "20 illustrated clinical problem scenarios",
      "Potential contributing factor matrices",
      "Functional displacement assessment guides",
      "Common evaluation errors",
      "Follow-up decision worksheets",
    ],
    why: "Quickly locate the relevant educational reference when reviewing an unstable denture case.",
  },
  {
    number: 3,
    title: "The Mandibular Anatomy Visual Reference Guide™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/dGFh7KhXKCfbpG6CzhSbqQ/BONUS_3_MANDIBULAR-EN.png",
    description: "An illustrated anatomical companion focused on structures relevant to mandibular complete denture treatment.",
    includes: [
      "Anatomical landmark illustrations",
      "Denture-bearing area maps",
      "Functional border reference diagrams",
      "Soft tissue evaluation illustrations",
      "Anatomical variation reference sheets",
    ],
    why: "Review important anatomical relationships without searching through multiple textbooks.",
  },
  {
    number: 4,
    title: "The Complete Denture Clinical Decision Flowcharts™",
    value: 39,
    image: "https://cdn.magicpatterns.com/uploads/hcaM5kvhGo5CcwXXHi9TFv/BONUS_4_MANDIBULAR-EN.png",
    description: "A collection of printable flowcharts designed to support structured clinical reasoning.",
    includes: [
      "Initial assessment pathways",
      "Stability evaluation flowcharts",
      "Impression planning decision aids",
      "Occlusal assessment pathways",
      "Complex case evaluation maps",
      "Post-delivery reassessment sequences",
    ],
    why: "Turn complex information into clear, visual decision pathways.",
  },
  {
    number: 5,
    title: "The 15-Minute Mandibular Case Planning System™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/6wn7ueqbQeY4JiVwmZHrw8/BONUS_5_MANDIBULAR-EN.png",
    description: "A focused case-organization workbook built around a practical 15-minute review sequence.",
    includes: [
      "Printable 15-minute assessment template",
      "Case summary worksheet",
      "Clinical priority checklist",
      "Missing-information tracker",
      "Treatment planning documentation sheet",
    ],
    why: "A repeatable way to organize case information without overlooking important considerations.",
  },
];

export const workflowSteps = [
  { minutes: "1–3", text: "Review the chief complaint and relevant patient history." },
  { minutes: "4–6", text: "Organize anatomical and tissue findings." },
  { minutes: "7–9", text: "Identify relevant stability and retention considerations." },
  { minutes: "10–12", text: "Review functional, impression, and occlusal factors." },
  { minutes: "13–15", text: "Document working hypotheses, missing information, and next assessment priorities." },
];

export const valueStack = [
  { label: "6-Volume Mandibular Complete Denture Blueprint™", value: 197 },
  { label: "Bonus #1: Case Assessment Workbook™", value: 37 },
  { label: "Bonus #2: Instability Troubleshooting Atlas™", value: 47 },
  { label: "Bonus #3: Anatomy Visual Reference Guide™", value: 29 },
  { label: "Bonus #4: Clinical Decision Flowcharts™", value: 39 },
  { label: "Bonus #5: 15-Minute Case Planning System™", value: 47 },
];

export const offerIncludes = [
  "Complete 6-volume digital collection",
  "All 5 professional bonuses",
  "30+ practical clinical decision tools",
  "Downloadable PDF resources",
  "Lifetime access to your purchased files",
  "Educational clinical reference material",
];

export const testimonials = [
  {
    name: "Dr. Elena Rodríguez",
    role: "General Dentist",
    topic: "Clinical organization",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/0176b1ba-75c5-49a9-a491-fe274a944b44.jpg",
    quote: "The biggest benefit for me would be having the assessment process organized in one place. Instead of reviewing scattered information, I could follow a clear sequence to identify which factors deserve closer attention.",
  },
  {
    name: "Dr. Martín Salazar",
    role: "Prosthodontist",
    topic: "Anatomical assessment",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/6d5e2c2f-f55b-41c2-9fd0-c8428cde3ca9.jpg",
    quote: "The anatomical diagrams and assessment checklists would be especially useful when reviewing patients with severely resorbed mandibular ridges.",
  },
  {
    name: "Dr. Camila Herrera",
    role: "Restorative Dentist",
    topic: "Treatment planning",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/27a1ddf8-b5e3-498e-9b1a-a2022f37ca6a.jpg",
    quote: "I like the idea of having a structured planning reference that connects anatomy, functional assessment, impression considerations, and occlusion.",
  },
  {
    name: "Dr. Roberto Vidal",
    role: "Removable Prosthodontics",
    topic: "Troubleshooting",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/efaa2068-36f4-4d84-ae9c-c1bff5e9fc68.jpg",
    quote: "The troubleshooting flowcharts would make it easier to organize my thinking when a patient returns with complaints about lower denture instability.",
  },
  {
    name: "Lucía Paredes",
    role: "Dental Technician",
    topic: "Clinical reference",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/55213689-89f6-483e-8570-a9923a623f81.jpg",
    quote: "A concise clinical reference like this could be helpful for reviewing important considerations before challenging mandibular denture appointments.",
  },
];

export const faqs = [
  {
    q: "What exactly is the Mandibular Complete Denture Blueprint™?",
    a: "It's a six-volume digital educational collection focused on mandibular complete denture assessment, stability, retention, functional considerations, and treatment planning. It also includes five professional bonuses and more than 30 practical clinical decision tools.",
  },
  {
    q: "Is this a video course?",
    a: "No. This is a collection of digital PDF resources designed for professional reading, review, and consultation. You can study the material at your own pace and return to specific sections whenever needed.",
  },
  {
    q: "Who is this product designed for?",
    a: "The collection is intended primarily for licensed dentists, prosthodontists, and dental professionals involved in removable prosthodontics. Dental students and dental technicians may also find relevant educational material, subject to their training and professional scope.",
  },
  {
    q: "Can I use this material if I practice outside the United States?",
    a: "The educational principles may be relevant internationally. However, clinical procedures, professional responsibilities, material availability, and applicable regulations can differ by country. Always follow your local professional standards.",
  },
  {
    q: "Will this help me evaluate severely resorbed mandibular ridges?",
    a: "Yes. The collection includes educational frameworks for evaluating anatomical challenges associated with severe mandibular ridge resorption. It does not guarantee that conventional dentures will be suitable for every patient.",
  },
  {
    q: "Does this teach a guaranteed technique for achieving mandibular suction?",
    a: "No. The Blueprint reviews anatomical, functional, and prosthetic factors associated with stability and retention. Clinical outcomes depend on individual patient conditions and appropriate professional assessment.",
  },
  {
    q: "How long does it take to use the clinical planning system?",
    a: "The included 15-minute workflow is designed to help organize existing case information and identify further assessment priorities. It does not replace a complete patient examination or comprehensive treatment planning.",
  },
  {
    q: "Can I download the volumes and bonuses?",
    a: "Yes. The collection is delivered as downloadable PDF resources you can save and consult on any device.",
  },
  {
    q: "Is this a one-time payment?",
    a: "Yes. The listed purchase price is $27 USD, with no recurring subscription charges.",
  },
  {
    q: "Will I receive lifetime access?",
    a: "Yes. The offer includes lifetime access to the purchased digital resources, subject to the final delivery and access terms.",
  },
  {
    q: "Is there a refund guarantee?",
    a: "Please review the final refund terms displayed at checkout before purchasing.",
  },
  {
    q: "Does this replace formal prosthodontic education or clinical training?",
    a: "No. The Mandibular Complete Denture Blueprint™ is an educational decision-support resource. It does not replace formal education, supervised clinical training, patient-specific diagnosis, professional judgment, or applicable clinical standards.",
  },
];

export const finalChecklist = [
  "Review the patient's anatomical findings.",
  "Identify possible contributors to poor denture stability.",
  "Organize functional and impression considerations.",
  "Compare relevant occlusal factors.",
  "Recognize when additional evaluation may be necessary.",
  "Document a clearer treatment planning pathway.",
];

export const recentBuyers = [
  { name: "María José", country: "Mexico" },
  { name: "Carlos", country: "Colombia" },
  { name: "Valentina", country: "Argentina" },
  { name: "Andrés", country: "Chile" },
  { name: "Sofía", country: "Peru" },
  { name: "Javier", country: "Spain" },
  { name: "Daniela", country: "Ecuador" },
  { name: "Luis Fernando", country: "Guatemala" },
  { name: "Gabriela", country: "Venezuela" },
  { name: "Diego", country: "Uruguay" },
  { name: "Paola", country: "Dominican Republic" },
  { name: "Ricardo", country: "Costa Rica" },
  { name: "Ana Lucía", country: "Bolivia" },
  { name: "Fernando", country: "Paraguay" },
  { name: "Isabel", country: "Panama" },
  { name: "Mateo", country: "Puerto Rico" },
];
